// @ts-strict-ignore
import React, { useMemo } from 'react';
import type { Control, FieldErrors } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import {
  Box,
  Icon,
  IconName,
} from '@usealloy/component-library';

import { StyledTextField, StyledErrorText } from './styles';

export const MAX_INTEGER_ERROR = 'Max Accepted Value must be an integer';
export const MIN_INTEGER_ERROR = 'Min Accepted Value must be an integer';
export const MIN_MAX_COMPARE_ERROR = 'Min Accepted Value must be less than Max Accepted Value';

export type NumberRangeFieldProps = {
  control: Control<any>;
  errors: FieldErrors;
  isDecimal?: boolean;
  disabled?: boolean;
};

const NumberRangeField = ({
  control, errors, isDecimal = false, disabled = false,
}: NumberRangeFieldProps) => {
  const {
    validValueMin: validValueMinError,
    validValueMax: validValueMaxError,
  } = errors;

  const isALessThanOrEqualToB = (a, b) => (isDecimal
    ? parseFloat(a) <= parseFloat(b)
    : parseInt(a, 10) <= parseInt(b, 10));

  const redBorderIfError = useMemo(() => (
    validValueMinError || validValueMaxError
      ? { border: '1px solid var(--cl-border-error)' }
      : {}
  ), [validValueMaxError, validValueMinError]);

  return (
    <Box direction="column" style={{ flex: '1 1 200%' }} gap={2}>
      <Box>
        <Controller
          control={control}
          name="validValueMin"
          defaultValue=""
          rules={{
            validate: (value, { validValueMax }) => {
              if ((value && !isDecimal) && value.indexOf('.') !== -1) {
                return MIN_INTEGER_ERROR;
              }
              if ((value && validValueMax) && !isALessThanOrEqualToB(value, validValueMax)) {
                return MIN_MAX_COMPARE_ERROR;
              }
              return null;
            },
            deps: ['validValueMax', 'defaultValue'],
          }}
          render={({ field: { ref: fieldRef, value, ...field } }) => (
            <StyledTextField
              {...field}
              value={value ?? ''}
              ref={fieldRef}
              type="number"
              label="Min Accepted Value"
              placeholder="any"
              step={isDecimal ? 0.1 : 1}
              style={redBorderIfError}
              disabled={disabled}
            />
          )}
        />
        <Box
          direction="column"
          style={{
            width: 'var(--cl-space-5)',
            paddingTop: 'var(--cl-space-5)',
          }}
        >
          <Icon icon={IconName.Minus} />
        </Box>
        <Controller
          control={control}
          name="validValueMax"
          defaultValue=""
          rules={{
            validate: (value, { validValueMin }) => {
              if ((value && !isDecimal) && value.indexOf('.') !== -1) {
                return MAX_INTEGER_ERROR;
              }
              if ((value && validValueMin) && !isALessThanOrEqualToB(validValueMin, value)) {
                return MIN_MAX_COMPARE_ERROR;
              }
              return null;
            },
            deps: ['validValueMin', 'defaultValue'],
          }}
          render={({ field: { ref: fieldRef, value, ...field } }) => (
            <StyledTextField
              {...field}
              value={value ?? ''}
              ref={fieldRef}
              type="number"
              label="Max Accepted Value"
              placeholder="any"
              step={isDecimal ? 0.1 : 1}
              style={redBorderIfError}
              disabled={disabled}
            />
          )}
        />
      </Box>
      {(validValueMinError || validValueMaxError) && (
        <Box justifyContent="flex-start">
          <StyledErrorText>
            {/* @ts-expect-error - TODO: fix this */}
            {validValueMinError?.message || validValueMaxError?.message}
          </StyledErrorText>
        </Box>
      )}
    </Box>
  );
};

export default NumberRangeField;
